import React, { useState } from "react";
import Button from "../../component/button/Button";
import InputText from "../../component/inputText/InputText";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import styles from "./Login.module.css";
import { loginPostApi } from "../../api/HttpApis";
import { Link, useNavigate } from "react-router-dom";
import { showToast } from "../../utils/Toastify/Toastify";
import { useTenant } from "../../context/TenantContext";

// Pick the landing page for an admin-level user based on what they can view.
// A full admin (analytics.view) lands on the dashboard; a limited staff member
// lands on the first module they're allowed to see, so they never open a page
// their role can't access. Falls back to dashboard if nothing matches.
export const landingRouteForAdmin = (permissions = {}) => {
  const can = (mod) => Boolean(permissions?.[mod]?.view);
  if (can("analytics")) return "/mainlayout/dashboard";
  const firstAccessible = [
    ["content", "/mainlayout/movie"],
    ["series", "/mainlayout/series"],
    ["media", "/mainlayout/media-library"],
    ["livetv", "/mainlayout/live-tv-channels"],
    ["categories", "/mainlayout/experience-builder"],
    ["users", "/mainlayout/user-management"],
    ["monetization", "/mainlayout/ad-management"],
    ["settings", "/mainlayout/settings"],
  ].find(([mod]) => can(mod));
  return firstAccessible ? firstAccessible[1] : "/mainlayout/dashboard";
};

function Login() {
  const { logo, hasFeature } = useTenant();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePassword = (e) => {
    const { value } = e.target;
    setFormData({ ...formData, password: value });
  };

  const handleSignUp = async () => {
    if (!formData.email || !formData.password) {
      showToast("Email and password are required.", "warning");
      return;
    }
    try {
      setLoading(true);
      const response = await loginPostApi({
        email: formData.email,
        password: formData.password,
      });
      const role = response?.data?.data?.user?.role;

      const token = response?.data?.data?.token;
      localStorage.setItem("userInfo", JSON.stringify(response?.data?.data));
      // Persist the back-office permission map for RBAC UI gating. Empty object
      // is fine (e.g. a staff member with no granted permissions).
      const permissions = response?.data?.data?.user?.permissions || {};
      localStorage.setItem("permissions", JSON.stringify(permissions));
      const refreshToken = response?.data?.data?.refresh_token;
      const expireDate = response?.data?.data?.expire_date;
      localStorage.setItem("refreshToken", refreshToken);
      // Without this the proactive refresher never runs after login and the
      // first expiry is only caught by a failed (401) request.
      localStorage.setItem("expireDate", expireDate);

      // IMPORTANT: write ALL auth state to localStorage BEFORE navigating, so the
      // sidebar/RBAC context read a fully-populated store on their first render.
      // (Navigating first caused a flash where role/token weren't set yet, so the
      // menu collapsed to just Dashboard until a manual reload.)
      // `role` is the LEGACY account type, so a Subscriber granted a CMS role
      // arrives here as "Subscriber". The old condition listed only
      // Creator/Admin and had no else, so such a person got total silence: no
      // token written, no redirect, no error — the login button simply looked
      // dead. The backend had already authenticated them and returned their
      // merged permissions.
      //
      // Anyone the backend let through belongs in the CMS; it refuses the rest
      // with a 403 that lands in the catch below. So the test is "did we get a
      // token", not "which account type is this".
      if (token) {
        localStorage.setItem("role", role);
        // `role` is the LEGACY account type. A granted Creator/Subscriber
        // keeps that type but belongs in the back-office UI, so the sidebar
        // needs this flag — keying the section set off the role string alone
        // showed a granted Subscriber the creator menu, with no link to the
        // screens their grant actually covers.
        localStorage.setItem(
          "isGranted",
          response?.data?.data?.user?.isGranted ? "1" : "0"
        );
        localStorage.setItem("token", token);
        window.dispatchEvent(new Event("storage"));
        navigate(
          role === "Creator"
            ? "/mainlayout/creator-dashboard"
            : landingRouteForAdmin(permissions)
        );
      } else {
        // Belt and braces: never leave the operator staring at an unchanged
        // login form with no explanation.
        showToast("Could not sign you in. Please try again.", "error");
      }
      setLoading(false);

    } catch (error) {
      const response = error?.response?.data;
      if (response?.errors) {
        const fieldErrors = Object.values(response.errors).flat();
        if (fieldErrors.length > 0) {
          showToast(fieldErrors[0], "error");
        } else if (response?.message) {
          showToast(response.message, "error");
        }
      } else if (response?.message) {
        showToast(String(response.message), "error");
      } else if (error?.message) {
        showToast(error.message, "error");
      } else {
        showToast("Something went wrong. Please try again.", "error");
      }
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          {hasFeature("content_partners_portal") && (
            <>
              <p className={styles.paraTextHeading}>Want to be a creator?</p>
              <div
                className={styles.signUpText}
                onClick={() => navigate("/creator-studio")}
              >
                <u> Sign Up </u>
              </div>
            </>
          )}
          {/* <FaUser className={styles.userIcon} /> */}
        </div>
        <img src={logo} alt="Logo" className={styles.logo} />

        <div className={styles.contain}>
          <p className={styles.loginText}>Login </p>
          <div className={styles.inputGroup}>
            {/* <p className={styles.loginText}>Login </p> */}
            {/* Email Input */}
            <InputText
              type="text"
              name="email"
              autofocus={true}
              placeholder="Email"
              className={styles.inputBox}
              value={formData.email}
              variant="dark"
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSignUp();
                }
              }}
            />

            {/* Password Input with Eye Icon */}
            <div className={styles.passwordWrapper}>
              <InputText
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="password"
                className={styles.inputBox}
                value={formData.password}
                variant="dark"
                onChange={handlePassword}
                showToggle={false}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSignUp();
                  }
                }}
              />
              <span
                className={styles.eyeIcon}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            {/* Login Button */}
            <Button onClick={handleSignUp} loading={loading ? true : false}>
              Sign In
            </Button>
          </div>
        </div>

        {/* Same public Help Center / Contact Support as the creator login page. */}
        <nav className={styles.helpLinks} aria-label="Help">
          <Link to="/creator-help">Help Center &amp; FAQ</Link>
          <span className={styles.helpDot} aria-hidden="true">
            ·
          </span>
          <Link to="/creator-login?contact=1">Contact Support</Link>
        </nav>
      </div>
    </>
  );
}

export default Login;
